import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users, Truck, Clock, Briefcase, Building2 } from "lucide-react";
import { useFirms } from "@/contexts/FirmContext";

/**
 * Phase 5Z.19b — Dashboard Firmen-Karten
 *
 * Shows one stat card per visible firm. Hidden when only the default
 * firm exists (single-tenant feel). Card click sets the active firm
 * in the top-bar switcher.
 */
export default function FirmStatsCards() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { setActiveFirmId, activeFirmId } = useFirms();

  useEffect(() => {
    let alive = true;
    api.get("/firms/stats").then(({ data }) => {
      if (alive) setData(data);
    }).catch(() => alive && setData(null)).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  if (loading) return null;
  if (!data?.firms?.length || data.firms.length < 2) return null; // Hide for single-firm setups

  return (
    <section data-testid="dashboard-firm-cards" className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-sm font-semibold text-zinc-200 inline-flex items-center gap-2">
            <Building2 className="w-4 h-4 text-blue-400" />
            Firmen-Übersicht
          </h2>
          <p className="text-[11px] text-zinc-500 mt-0.5">Stand {data.month} · Klick filtert die ganze Plattform auf die gewählte Firma.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {data.firms.map((f) => {
          const active = activeFirmId === f.id;
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => setActiveFirmId(active ? null : f.id)}
              data-testid={`firm-card-${f.id}`}
              className={`text-left bg-zinc-950 border rounded-lg p-4 transition-all hover:border-zinc-700 ${
                active ? "border-blue-500/60 ring-1 ring-blue-500/30" : "border-zinc-800"
              }`}
            >
              <div className="flex items-center gap-2.5 mb-3">
                <span
                  className="w-1 h-9 rounded-full flex-shrink-0"
                  style={{ background: f.color || "#3b82f6" }}
                  aria-hidden
                />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-zinc-100 truncate" title={f.name}>
                    {f.name}
                  </div>
                  {f.legal_name && f.legal_name !== f.name && (
                    <div className="text-[10px] text-zinc-500 truncate">{f.legal_name}</div>
                  )}
                </div>
                {f.is_default && (
                  <span className="text-[9px] uppercase tracking-wider text-amber-400 font-semibold flex-shrink-0">std</span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Stat icon={Users} label="Aktive Fahrer"
                      value={f.employees_active}
                      sub={f.employees_active !== f.employees_total ? `${f.employees_total} ges.` : null}
                      testid={`firm-card-emp-${f.id}`}/>
                <Stat icon={Clock} label={`Std. ${data.month.slice(0,2)}/`}
                      value={f.time_hours_month.toLocaleString("de-AT", { maximumFractionDigits: 1 })}
                      sub={`${f.time_entries_month} Einträge`}
                      testid={`firm-card-hrs-${f.id}`}/>
                <Stat icon={Truck} label="Fahrzeuge"
                      value={f.vehicles_total}
                      testid={`firm-card-veh-${f.id}`}/>
                <Stat icon={Briefcase} label="Offene Aufträge"
                      value={f.orders_open}
                      testid={`firm-card-ord-${f.id}`}/>
              </div>

              {active && (
                <div className="mt-3 pt-2 border-t border-zinc-800 text-[10px] text-blue-400 font-medium uppercase tracking-wider">
                  ✓ Diese Firma aktiv — Klick erneut um aufzuheben
                </div>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
}

function Stat({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="flex items-center gap-2 p-2 bg-zinc-900/40 rounded" data-testid={testid}>
      <Icon className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 truncate">{label}</div>
        <div className="flex items-baseline gap-1">
          <span className="text-base font-bold tabular-nums text-zinc-100">{value}</span>
          {sub && <span className="text-[9px] text-zinc-500">{sub}</span>}
        </div>
      </div>
    </div>
  );
}
